import { useState, useEffect } from "react"
import { database, auth } from "../firebase"
import { ref, onValue, off } from "firebase/database"
import { onAuthStateChanged } from "firebase/auth"
import ReactDOMServer from "react-dom/server"

// Helper: Extract city from address
const getCityFromAddress = (address) => {
  if (!address) return "Unknown City"
  const addressParts = address.trim().split(/\s+/)
  return addressParts.length > 0 ? addressParts[addressParts.length - 1] : "Unknown City"
}

// Filter Section Component
const FilterSection = ({ title, options, selected, onChange }) => {
  const handleCheckboxChange = (option) => {
    if (option === options[0]) {
      // "All" option
      if (selected.includes(option)) {
        onChange([])
      } else {
        onChange([...options])
      }
    } else {
      const newSelected = selected.includes(option)
        ? selected.filter((item) => item !== option && item !== options[0])
        : [...selected.filter((item) => item !== options[0]), option]
      if (newSelected.length === options.length - 1) {
        onChange([options[0], ...newSelected])
      } else {
        onChange(newSelected)
      }
    }
  }

  return (
    <div
      style={{
        backgroundColor: "#f8fafc",
        padding: "16px",
        borderRadius: "8px",
        border: "1px solid #e2e8f0",
        boxShadow: "0 2px 4px rgba(0, 0, 0, 0.05)",
        transition: "all 0.3s ease",
        maxWidth: "220px",
        marginBottom: "16px",
      }}
    >
      <h2
        style={{
          fontSize: "16px",
          fontWeight: "600",
          color: "#1e293b",
          marginBottom: "8px",
          borderBottom: "2px solid #3b82f6",
          paddingBottom: "6px",
        }}
      >
        {title}
      </h2>
      <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
        {options.map((option) => (
          <label
            key={option}
            style={{
              display: "flex",
              alignItems: "center",
              gap: "8px",
              cursor: "pointer",
              padding: "6px",
              borderRadius: "6px",
              transition: "background-color 0.2s",
              backgroundColor: selected.includes(option) ? "#eff6ff" : "transparent",
            }}
            onMouseEnter={(e) => {
              if (!selected.includes(option)) {
                e.currentTarget.style.backgroundColor = "#f1f5f9"
              }
            }}
            onMouseLeave={(e) => {
              if (!selected.includes(option)) {
                e.currentTarget.style.backgroundColor = "transparent"
              }
            }}
          >
            <input
              type="checkbox"
              checked={selected.includes(option)}
              onChange={() => handleCheckboxChange(option)}
              style={{
                height: "14px",
                width: "14px",
                accentColor: "#3b82f6",
                cursor: "pointer",
              }}
            />
            <span
              style={{
                fontSize: "14px",
                color: "#475569",
                userSelect: "none",
                fontWeight: selected.includes(option) ? "500" : "400",
              }}
            >
              {option}
            </span>
          </label>
        ))}
      </div>
    </div>
  )
}

// Sales Product Report Display Component
const SalesProductReportDisplay = ({ formData, filters, branchProfile, salesData, productDetails }) => {
  // Helper function to parse date
  const parseInvoiceDate = (dateString) => {
    if (!dateString) return null
    if (/^\d{2}[-/]\d{2}[-/]\d{4}$/.test(dateString)) {
      const parts = dateString.split(/[-/]/)
      return new Date(Number.parseInt(parts[2], 10), Number.parseInt(parts[1], 10) - 1, Number.parseInt(parts[0], 10))
    }
    if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
      const parts = dateString.split("-")
      return new Date(Number.parseInt(parts[0], 10), Number.parseInt(parts[1], 10) - 1, Number.parseInt(parts[2], 10))
    }
    return new Date(dateString)
  }

  // Format date to DD/MM/YYYY for display
  const formatDisplayDate = (dateString) => {
    const date = parseInvoiceDate(dateString)
    return !date || isNaN(date.getTime())
      ? "N/A"
      : date.toLocaleDateString("en-GB", {
          day: "2-digit",
          month: "2-digit",
          year: "numeric",
        })
  }

  // Get product details from productDetails array
  const getProductDetails = (productName) => {
    if (!productDetails || !productName) return {}
    const product = productDetails.find(p => p.productName === productName)
    return product || {}
  }

  // Filter salesData by selected criteria
  let filteredSalesData = [...salesData]

  // Apply date filter
  if (formData.fromDate && formData.toDate) {
    const fromDate = new Date(formData.fromDate)
    const toDate = new Date(formData.toDate)
    toDate.setHours(23, 59, 59, 999)

    filteredSalesData = filteredSalesData.filter((item) => {
      const itemDate = parseInvoiceDate(item.invoiceDate)
      return itemDate && !isNaN(itemDate.getTime()) && itemDate >= fromDate && itemDate <= toDate
    })
  }

  // Apply store filter
  if (!filters.store.includes("All Stores")) {
    filteredSalesData = filteredSalesData.filter((item) => filters.store.includes(item.store))
  }

  // Apply customer filter
  if (!filters.customer.includes("All Customer")) {
    filteredSalesData = filteredSalesData.filter((item) => filters.customer.includes(item.customer))
  }

  // Apply group filter
  if (!filters.group.includes("All Groups")) {
    filteredSalesData = filteredSalesData.filter((item) => filters.group.includes(item.groupName))
  }

  // Apply product filter
  if (!filters.product.includes("All Products")) {
    filteredSalesData = filteredSalesData.filter((item) => filters.product.includes(item.productName))
  }

  // Process data for product-wise report with proper GST calculations
    const groupedData = filteredSalesData.reduce((acc, item) => {
    const key = item.productName
    const productInfo = getProductDetails(item.productName)

    // GST slab and rates
    let gstSlab = 0
    let cgstPercent = 0
    let sgstPercent = 0
    let igstPercent = 0
    let cessPercent = 0

    // Prefer productInfo.gstRate if available, else sum item rates
    if (productInfo.gstRate) {
      gstSlab = Number.parseFloat(productInfo.gstRate) || 0
    } else {
      gstSlab = (Number.parseFloat(item.cgstRate) || 0) + (Number.parseFloat(item.sgstRate) || 0) + (Number.parseFloat(item.igstRate) || 0)
    }

    // Correct GST split: If GST SLAB is 5%, CGST % = 2.50%, SGST % = 2.50%
    if (gstSlab > 0) {
      cgstPercent = +(gstSlab / 2).toFixed(2)
      sgstPercent = +(gstSlab / 2).toFixed(2)
      igstPercent = 0
    } else {
      cgstPercent = Number.parseFloat(item.cgstRate) || 0
      sgstPercent = Number.parseFloat(item.sgstRate) || 0
      igstPercent = Number.parseFloat(item.igstRate) || 0
    }
    cessPercent = Number.parseFloat(item.cessRate) || productInfo.cessRate || 0

    if (!acc[key]) {
      acc[key] = {
        productName: key,
        hsnCode: productInfo.hsnSacCode || item.hsnCode || item.hsnSacCode || "N/A",
        invoices: [],
        gstSlab: gstSlab,
        cgstPercent: cgstPercent,
        cgstAmt: 0,
        sgstPercent: sgstPercent,
        sgstAmt: 0,
        igstPercent: igstPercent,
        igstAmt: 0,
        gstAmt: 0,
        cessPercent: cessPercent,
        cessAmt: 0,
        discAmount: 0,
        grossAmount: 0,
        netAmount: 0,
        count: 0,
      }
    }

    // Calculate amounts
    const qty = Number.parseFloat(item.qty) || 0
    const rate = Number.parseFloat(item.pRate) || Number.parseFloat(item.unitRate) || 0
    const discAmt = Number.parseFloat(item.discAmt) || 0
    const grossAmt = Number.parseFloat(item.grossAmt) || (qty * rate)
    const taxableValue = grossAmt - discAmt

    // Calculate GST amounts using corrected split
    let cgstAmt = 0, sgstAmt = 0, igstAmt = 0
    if (igstPercent > 0) {
      igstAmt = (taxableValue * igstPercent) / 100
    } else {
      cgstAmt = (taxableValue * cgstPercent) / 100
      sgstAmt = (taxableValue * sgstPercent) / 100
    }
    const cessAmt = (taxableValue * cessPercent) / 100
    const netValue = taxableValue + cgstAmt + sgstAmt + igstAmt + cessAmt

    // Add to invoice list
    acc[key].invoices.push({
      invoiceNo: item.invoiceNo,
      invoiceDate: item.invoiceDate,
      qty: qty,
      rate: rate,
      grossAmt: grossAmt,
      discAmt: discAmt,
      cgstAmt: cgstAmt,
      sgstAmt: sgstAmt,
      igstAmt: igstAmt,
      cessAmt: cessAmt,
      netValue: netValue,
      discAmount: discAmt
    })

    // Update totals
    acc[key].gstSlab = gstSlab
    acc[key].cgstPercent = cgstPercent
    acc[key].cgstAmt += cgstAmt
    acc[key].sgstPercent = sgstPercent
    acc[key].sgstAmt += sgstAmt
    acc[key].igstPercent = igstPercent
    acc[key].igstAmt += igstAmt
    acc[key].cessPercent = cessPercent
    acc[key].cessAmt += cessAmt
    acc[key].discAmount += discAmt
    acc[key].grossAmount += grossAmt
    acc[key].netAmount += netValue
    acc[key].gstAmt = acc[key].cgstAmt + acc[key].sgstAmt + acc[key].igstAmt
    acc[key].count += 1

    return acc
  }, {})

  const displayData = Object.values(groupedData)
  displayData.sort((a, b) => a.productName.localeCompare(b.productName))

  // Calculate totals
  const totalCgstAmt = displayData.reduce((sum, item) => sum + (Number.parseFloat(item.cgstAmt) || 0), 0)
  const totalSgstAmt = displayData.reduce((sum, item) => sum + (Number.parseFloat(item.sgstAmt) || 0), 0)
  const totalIgstAmt = displayData.reduce((sum, item) => sum + (Number.parseFloat(item.igstAmt) || 0), 0)
  const totalGstAmt = totalCgstAmt + totalSgstAmt + totalIgstAmt
  const totalCessAmt = displayData.reduce((sum, item) => sum + (Number.parseFloat(item.cessAmt) || 0), 0)
  const totalDiscAmount = displayData.reduce(
    (sum, item) => sum + (Number.parseFloat(item.discAmount) || 0),
    0,
  )
  const totalGrossAmount = displayData.reduce(
    (sum, item) => sum + (Number.parseFloat(item.grossAmount) || 0),
    0,
  )
  const totalNetAmount = displayData.reduce(
    (sum, item) => sum + (Number.parseFloat(item.netAmount) || 0),
    0,
  )

  const city = branchProfile.city || getCityFromAddress(branchProfile.address)

  const renderTableHeaders = () => {
    return (
      <tr style={{ backgroundColor: '#f1f5f9', borderBottom: '2px solid #3b82f6' }}>
        <th style={{ border: '1px solid #cbd5e1', padding: '12px', textAlign: 'center', fontWeight: '600', fontSize: '12px' }}>
          S.No
        </th>
        <th style={{ border: '1px solid #cbd5e1', padding: '12px', textAlign: 'center', fontWeight: '600', fontSize: '12px' }}>
          Product Name
        </th>
        <th style={{ border: '1px solid #cbd5e1', padding: '12px', textAlign: 'center', fontWeight: '600', fontSize: '12px' }}>
          HSN Code
        </th>
        <th style={{ border: '1px solid #cbd5e1', padding: '12px', textAlign: 'center', fontWeight: '600', fontSize: '12px' }}>
          GST SLAB
        </th>
        <th style={{ border: '1px solid #cbd5e1', padding: '12px', textAlign: 'center', fontWeight: '600', fontSize: '12px' }}>
          CGST %
        </th>
        <th style={{ border: '1px solid #cbd5e1', padding: '12px', textAlign: 'center', fontWeight: '600', fontSize: '12px' }}>
          CGST AMT
        </th>
        <th style={{ border: '1px solid #cbd5e1', padding: '12px', textAlign: 'center', fontWeight: '600', fontSize: '12px' }}>
          SGST %
        </th>
        <th style={{ border: '1px solid #cbd5e1', padding: '12px', textAlign: 'center', fontWeight: '600', fontSize: '12px' }}>
          SGST AMT
        </th>
        <th style={{ border: '1px solid #cbd5e1', padding: '12px', textAlign: 'center', fontWeight: '600', fontSize: '12px' }}>
          IGST %
        </th>
        <th style={{ border: '1px solid #cbd5e1', padding: '12px', textAlign: 'center', fontWeight: '600', fontSize: '12px' }}>
          IGST AMT
        </th>
        <th style={{ border: '1px solid #cbd5e1', padding: '12px', textAlign: 'center', fontWeight: '600', fontSize: '12px' }}>
          GST AMT
        </th>
        <th style={{ border: '1px solid #cbd5e1', padding: '12px', textAlign: 'center', fontWeight: '600', fontSize: '12px' }}>
          CESS %
        </th>
        <th style={{ border: '1px solid #cbd5e1', padding: '12px', textAlign: 'center', fontWeight: '600', fontSize: '12px' }}>
          CESS AMT
        </th>
        <th style={{ border: '1px solid #cbd5e1', padding: '12px', textAlign: 'center', fontWeight: '600', fontSize: '12px' }}>
          Discount Amount
        </th>
        <th style={{ border: '1px solid #cbd5e1', padding: '12px', textAlign: 'center', fontWeight: '600', fontSize: '12px' }}>
          Gross Amount
        </th>
        <th style={{ border: '1px solid #cbd5e1', padding: '12px', textAlign: 'center', fontWeight: '600', fontSize: '12px' }}>
          Net Amount
        </th>
      </tr>
    )
  }

  const renderTableRows = () => {
    if (displayData.length === 0) {
      return (
        <tr>
          <td
            colSpan={16}
            style={{
              border: '1px solid #cbd5e1',
              padding: '30px',
              textAlign: 'center',
              color: '#64748b',
              fontStyle: 'italic',
              fontSize: '14px',
              backgroundColor: '#f8fafc',
            }}
          >
            No sales data found for the selected criteria
          </td>
        </tr>
      )
    }

    return displayData.map((item, index) => (
      <tr key={item.productName} style={{ backgroundColor: index % 2 === 0 ? '#ffffff' : '#f8fafc' }}>
        <td style={{ border: '1px solid #cbd5e1', padding: '8px', textAlign: 'center', fontSize: '11px' }}>
          {index + 1}
        </td>
        <td style={{ border: '1px solid #cbd5e1', padding: '8px', textAlign: 'left', fontSize: '11px' }}>
          {item.productName}
        </td>
        <td style={{ border: '1px solid #cbd5e1', padding: '8px', textAlign: 'center', fontSize: '11px' }}>
          {item.hsnCode || "N/A"}
        </td>
        <td style={{ border: '1px solid #cbd5e1', padding: '8px', textAlign: 'center', fontSize: '11px' }}>
          {item.gstSlab.toFixed(2)}%
        </td>
        <td style={{ border: '1px solid #cbd5e1', padding: '8px', textAlign: 'right', fontSize: '11px' }}>
          {item.cgstPercent.toFixed(2)}%
        </td>
        <td style={{ border: '1px solid #cbd5e1', padding: '8px', textAlign: 'right', fontSize: '11px' }}>
          {item.cgstAmt.toFixed(2)}
        </td>
        <td style={{ border: '1px solid #cbd5e1', padding: '8px', textAlign: 'right', fontSize: '11px' }}>
          {item.sgstPercent.toFixed(2)}%
        </td>
        <td style={{ border: '1px solid #cbd5e1', padding: '8px', textAlign: 'right', fontSize: '11px' }}>
          {item.sgstAmt.toFixed(2)}
        </td>
        <td style={{ border: '1px solid #cbd5e1', padding: '8px', textAlign: 'right', fontSize: '11px' }}>
          {item.igstPercent.toFixed(2)}%
        </td>
        <td style={{ border: '1px solid #cbd5e1', padding: '8px', textAlign: 'right', fontSize: '11px' }}>
          {item.igstAmt.toFixed(2)}
        </td>
        <td style={{ border: '1px solid #cbd5e1', padding: '8px', textAlign: 'right', fontSize: '11px' }}>
          {item.gstAmt.toFixed(2)}
        </td>
        <td style={{ border: '1px solid #cbd5e1', padding: '8px', textAlign: 'right', fontSize: '11px' }}>
          {item.cessPercent.toFixed(2)}%
        </td>
        <td style={{ border: '1px solid #cbd5e1', padding: '8px', textAlign: 'right', fontSize: '11px' }}>
          {item.cessAmt.toFixed(2)}
        </td>
        <td style={{ border: '1px solid #cbd5e1', padding: '8px', textAlign: 'right', fontSize: '11px' }}>
          {item.discAmount.toFixed(2)}
        </td>
        <td style={{ border: '1px solid #cbd5e1', padding: '8px', textAlign: 'right', fontSize: '11px' }}>
          {item.grossAmount.toFixed(2)}
        </td>
        <td style={{ border: '1px solid #cbd5e1', padding: '8px', textAlign: 'right', fontSize: '11px' }}>
          {item.netAmount.toFixed(2)}
        </td>
      </tr>
    ))
  }

  const renderTotalRow = () => {
    if (displayData.length === 0) return null

    return (
      <tr style={{ backgroundColor: '#fef3c7', fontWeight: 'bold', borderTop: '2px solid #f59e0b' }}>
        <td
          colSpan="5"
          style={{
            border: '1px solid #cbd5e1',
            padding: '10px',
            textAlign: 'center',
            fontSize: '12px',
            fontWeight: '600',
          }}
        >
          Total
        </td>
        <td
          style={{
            border: '1px solid #cbd5e1',
            padding: '10px',
            textAlign: 'right',
            fontSize: '12px',
            fontWeight: '600',
          }}
        >
          {totalCgstAmt.toFixed(2)}
        </td>
        <td
          style={{
            border: '1px solid #cbd5e1',
            padding: '10px',
            textAlign: 'right',
            fontSize: '12px',
            fontWeight: '600',
          }}
        >
        </td>
        <td
          style={{
            border: '1px solid #cbd5e1',
            padding: '10px',
            textAlign: 'right',
            fontSize: '12px',
            fontWeight: '600',
          }}
        >
          {totalSgstAmt.toFixed(2)}
        </td>
        <td
          style={{
            border: '1px solid #cbd5e1',
            padding: '10px',
            textAlign: 'right',
            fontSize: '12px',
            fontWeight: '600',
          }}
        >
        </td>
        <td
          style={{
            border: '1px solid #cbd5e1',
            padding: '10px',
            textAlign: 'right',
            fontSize: '12px',
            fontWeight: '600',
          }}
        >
          {totalIgstAmt.toFixed(2)}
        </td>
        <td
          style={{
            border: '1px solid #cbd5e1',
            padding: '10px',
            textAlign: 'right',
            fontSize: '12px',
            fontWeight: '600',
          }}
        >
          {totalGstAmt.toFixed(2)}
        </td>
        <td
          style={{
            border: '1px solid #cbd5e1',
            padding: '10px',
            textAlign: 'right',
            fontSize: '12px',
            fontWeight: '600',
          }}
        >
        </td>
        <td
          style={{
            border: '1px solid #cbd5e1',
            padding: '10px',
            textAlign: 'right',
            fontSize: '12px',
            fontWeight: '600',
          }}
        >
          {totalCessAmt.toFixed(2)}
        </td>
        <td
          style={{
            border: '1px solid #cbd5e1',
            padding: '10px',
            textAlign: 'right',
            fontSize: '12px',
            fontWeight: '600',
          }}
        >
          {totalDiscAmount.toFixed(2)}
        </td>
        <td
          style={{
            border: '1px solid #cbd5e1',
            padding: '10px',
            textAlign: 'right',
            fontSize: '12px',
            fontWeight: '600',
          }}
        >
          {totalGrossAmount.toFixed(2)}
        </td>
        <td
          style={{
            border: '1px solid #cbd5e1',
            padding: '10px',
            textAlign: 'right',
            fontSize: '12px',
            fontWeight: '600',
          }}
        >
          {totalNetAmount.toFixed(2)}
        </td>
      </tr>
    )
  }

  const renderGrandTotalRow = () => {
    if (displayData.length === 0) return null

    return (
      <tr style={{ backgroundColor: '#fee2e2', fontWeight: 'bold', borderTop: '2px solid #dc2626' }}>
        <td
          colSpan="5"
          style={{
            border: '1px solid #cbd5e1',
            padding: '10px',
            textAlign: 'center',
            fontSize: '12px',
            fontWeight: '600',
          }}
        >
          Grand Total
        </td>
        <td
          style={{
            border: '1px solid #cbd5e1',
            padding: '10px',
            textAlign: 'right',
            fontSize: '12px',
            fontWeight: '600',
          }}
        >
          {totalCgstAmt.toFixed(2)}
        </td>
        <td
          style={{
            border: '1px solid #cbd5e1',
            padding: '10px',
            textAlign: 'right',
            fontSize: '12px',
            fontWeight: '600',
          }}
        >
        </td>
        <td
          style={{
            border: '1px solid #cbd5e1',
            padding: '10px',
            textAlign: 'right',
            fontSize: '12px',
            fontWeight: '600',
          }}
        >
          {totalSgstAmt.toFixed(2)}
        </td>
        <td
          style={{
            border: '1px solid #cbd5e1',
            padding: '10px',
            textAlign: 'right',
            fontSize: '12px',
            fontWeight: '600',
          }}
        >
        </td>
        <td
          style={{
            border: '1px solid #cbd5e1',
            padding: '10px',
            textAlign: 'right',
            fontSize: '12px',
            fontWeight: '600',
          }}
        >
          {totalIgstAmt.toFixed(2)}
        </td>
        <td
          style={{
            border: '1px solid #cbd5e1',
            padding: '10px',
            textAlign: 'right',
            fontSize: '12px',
            fontWeight: '600',
          }}
        >
          {totalGstAmt.toFixed(2)}
        </td>
        <td
          style={{
            border: '1px solid #cbd5e1',
            padding: '10px',
            textAlign: 'right',
            fontSize: '12px',
            fontWeight: '600',
          }}
        >
        </td>
        <td
          style={{
            border: '1px solid #cbd5e1',
            padding: '10px',
            textAlign: 'right',
            fontSize: '12px',
            fontWeight: '600',
          }}
        >
          {totalCessAmt.toFixed(2)}
        </td>
        <td
          style={{
            border: '1px solid #cbd5e1',
            padding: '10px',
            textAlign: 'right',
            fontSize: '12px',
            fontWeight: '600',
          }}
        >
          {totalDiscAmount.toFixed(2)}
        </td>
        <td
          style={{
            border: '1px solid #cbd5e1',
            padding: '10px',
            textAlign: 'right',
            fontSize: '12px',
            fontWeight: '600',
          }}
        >
          {totalGrossAmount.toFixed(2)}
        </td>
        <td
          style={{
            border: '1px solid #cbd5e1',
            padding: '10px',
            textAlign: 'right',
            fontSize: '12px',
            fontWeight: '600',
          }}
        >
          {totalNetAmount.toFixed(2)}
        </td>
      </tr>
    )
  }

  return (
    <div
      style={{
        backgroundColor: 'white',
        padding: '30px',
        marginTop: '20px',
        border: '2px solid #3b82f6',
        borderRadius: '12px',
        fontFamily: 'Arial, sans-serif',
        fontSize: '12px',
        pageBreakInside: 'avoid',
        boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)',
      }}
      className="report-display"
    >
      {/* Header */}
      <div
        style={{
          textAlign: 'center',
          marginBottom: '30px',
          paddingBottom: '20px',
          borderBottom: '2px solid #e2e8f0',
        }}
      >
        <h1
          style={{
            fontSize: '24px',
            fontWeight: 'bold',
            margin: '0',
            marginBottom: '5px',
            color: '#1e293b',
            textShadow: '1px 1px 2px rgba(0,0,0,0.1)',
          }}
        >
          {branchProfile.branchName}
        </h1>
        <div style={{ fontSize: '16px', fontWeight: '600', color: '#3b82f6', marginBottom: '10px' }}>{city}</div>
        <div style={{ fontSize: '14px', color: '#475569', marginBottom: '10px' }}>
          Productwise Sales Annexure Report From : 
          {formData.fromDate ? new Date(formData.fromDate).toLocaleDateString('en-GB') : 'N/A'} To : 
          {formData.toDate ? new Date(formData.toDate).toLocaleDateString('en-GB') : 'N/A'}
        </div>
        <div style={{ fontSize: '14px', fontWeight: '600', color: '#1e293b' }}>
          Option: Abstract
        </div>
      </div>

      {/* Sales Table */}
      <div style={{ overflowX: 'auto' }}>
        <table
          style={{
            width: '100%',
            borderCollapse: 'collapse',
            fontSize: '11px',
            border: '2px solid #3b82f6',
            borderRadius: '8px',
            overflow: 'hidden',
            boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
          }}
        >
          <thead>{renderTableHeaders()}</thead>
          <tbody>
            {renderTableRows()}
            {renderTotalRow()}
            {renderGrandTotalRow()}
          </tbody>
        </table>
      </div>

      {/* Summary Section */}
      {displayData.length > 0 && (
        <div
          style={{
            marginTop: '30px',
            padding: '20px',
            backgroundColor: '#f8fafc',
            borderRadius: '8px',
            border: '1px solid #e2e8f0',
          }}
        >
          <h3
            style={{
              fontSize: '16px',
              fontWeight: '600',
              marginBottom: '15px',
              color: '#1e293b',
            }}
          >
            Summary
          </h3>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
              gap: '15px',
            }}
          >
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '12px', color: '#64748b' }}>Total Records</div>
              <div style={{ fontSize: '18px', fontWeight: '600', color: '#3b82f6' }}>{displayData.length}</div>
            </div>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '12px', color: '#64748b' }}>Total CGST</div>
              <div style={{ fontSize: '18px', fontWeight: '600', color: '#059669' }}>{totalCgstAmt.toFixed(2)}</div>
            </div>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '12px', color: '#64748b' }}>Total SGST</div>
              <div style={{ fontSize: '18px', fontWeight: '600', color: '#0891b2' }}>{totalSgstAmt.toFixed(2)}</div>
            </div>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '12px', color: '#64748b' }}>Total IGST</div>
              <div style={{ fontSize: '18px', fontWeight: '600', color: '#c2410c' }}>{totalIgstAmt.toFixed(2)}</div>
            </div>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '12px', color: '#64748b' }}>Total GST Amount</div>
              <div style={{ fontSize: '18px', fontWeight: '600', color: '#059669' }}>{totalGstAmt.toFixed(2)}</div>
            </div>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '12px', color: '#64748b' }}>Total CESS</div>
              <div style={{ fontSize: '18px', fontWeight: '600', color: '#dc2626' }}>{totalCessAmt.toFixed(2)}</div>
            </div>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '12px', color: '#64748b' }}>Total Discount</div>
              <div style={{ fontSize: '18px', fontWeight: '600', color: '#dc2626' }}>{totalDiscAmount.toFixed(2)}</div>
            </div>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '12px', color: '#64748b' }}>Gross Amount</div>
              <div style={{ fontSize: '18px', fontWeight: '600', color: '#7c3aed' }}>{totalGrossAmount.toFixed(2)}</div>
            </div>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '12px', color: '#64748b' }}>Net Amount</div>
              <div style={{ fontSize: '18px', fontWeight: '600', color: '#7c3aed' }}>{totalNetAmount.toFixed(2)}</div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

// Main Component
const SalesProductReportHSN_1A = ({ salesFormData }) => {
  const today = new Date()
  const [fromDate, setFromDate] = useState(today.toISOString().split("T")[0])
  const [toDate, setToDate] = useState(today.toISOString().split("T")[0])
  const [searchBy, setSearchBy] = useState("Productwise")
  const [filters, setFilters] = useState({
    store: ["All Stores"],
    customer: ["All Customer"],
    group: ["All Groups"],
    product: ["All Products"],
  })
  const [stores, setStores] = useState(["All Stores"])
  const [customers, setCustomers] = useState(["All Customer"])
  const [groups, setGroups] = useState(["All Groups"])
  const [products, setProducts] = useState(["All Products"])
  const [currentUser, setCurrentUser] = useState(null)
  const [loading, setLoading] = useState(true)
  const [showReport, setShowReport] = useState(false)
  const [branchProfile, setBranchProfile] = useState({
    branchName: "SUPREME TRADERS",
    address: "",
    phone: "",
    city: "",
  })
  const [salesData, setSalesData] = useState(salesFormData || [])
  const [productDetails, setProductDetails] = useState([])

  const encodeEmailForPath = (email) => {
    return email.replace(/[.#$[\]]/g, "_")
  }

  // Firebase Auth and Data Loading
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      setCurrentUser(user)
      if (user) {
        loadFirebaseData(user)
      }
      setLoading(false)
    })

    return () => unsubscribe()
  }, [])

  const loadFirebaseData = async (user) => {
    const encodedEmail = encodeEmailForPath(user.email)

    // Load Branch Profile
    const branchProfileRef = ref(database, `users/${encodedEmail}/branchProfiles`)
    onValue(branchProfileRef, (snapshot) => {
      const data = snapshot.val()
      if (data) {
        const profiles = Object.values(data)
        const latestProfile = profiles[profiles.length - 1]
        setBranchProfile({
          branchName: latestProfile.branchName || "SUPREME TRADERS",
          address: `${latestProfile.address1 || ""} ${latestProfile.address2 || ""}`.trim(),
          phone: latestProfile.mobileNo || "",
          city:
            latestProfile.city ||
            (latestProfile.address1 ? getCityFromAddress(latestProfile.address1) : "Unknown City"),
        })
      }
    })

    // Load Stores
    const storesRef = ref(database, `users/${encodedEmail}/stores`)
    onValue(storesRef, (snapshot) => {
      const storesData = ["All Stores"]
      if (snapshot.exists()) {
        snapshot.forEach((childSnapshot) => {
          const store = childSnapshot.val()
          if (store.storeName && !storesData.includes(store.storeName)) {
            storesData.push(store.storeName)
          }
        })
      }
      setStores(storesData)
    })

    // Load Customers
    const customersRef = ref(database, `users/${encodedEmail}/customers`)
    onValue(customersRef, (snapshot) => {
      const customersData = ["All Customer"]
      if (snapshot.exists()) {
        snapshot.forEach((childSnapshot) => {
          const customer = childSnapshot.val()
          if (customer.customerName && !customersData.includes(customer.customerName)) {
            customersData.push(customer.customerName)
          }
        })
      }
      setCustomers(customersData)
    })

    // Load Groups
    const groupsRef = ref(database, `users/${encodedEmail}/productGroups`)
    onValue(groupsRef, (snapshot) => {
      const groupsData = ["All Groups"]
      const groupsMapping = {}
      if (snapshot.exists()) {
        snapshot.forEach((childSnapshot) => {
          const group = childSnapshot.val()
          if (group.name && !groupsData.includes(group.name)) {
            groupsData.push(group.name)
            groupsMapping[childSnapshot.key] = group.name
            groupsMapping[group.name] = group.name
          }
        })
      }
      setGroups(groupsData)
      
      // Load products with groups
      loadProductsWithGroups(encodedEmail, groupsMapping)
    })

    return () => {
      off(branchProfileRef)
      off(storesRef)
      off(customersRef)
      off(groupsRef)
    }
  }

  const loadProductsWithGroups = (encodedEmail, groupsMapping) => {
    const productsRef = ref(database, `users/${encodedEmail}/products`)
    onValue(productsRef, (snapshot) => {
      const productsData = ["All Products"]
      const productDetailsArray = []
      
      if (snapshot.exists()) {
        snapshot.forEach((childSnapshot) => {
          const product = childSnapshot.val()
          if (product.productName && !productsData.includes(product.productName)) {
            productsData.push(product.productName)
            
            let groupName = 'No Group Assigned'
            if (product.groupName && product.groupName !== '') {
              groupName = product.groupName
            } else if (product.group && groupsMapping[product.group]) {
              groupName = groupsMapping[product.group]
            } else if (product.group && Object.values(groupsMapping).includes(product.group)) {
              groupName = product.group
            }
            
            productDetailsArray.push({
              productName: product.productName,
              hsnSacCode: product.hsnSacCode || product.hsnCode || product.hsn || "N/A",
              hsnCode: product.hsnSacCode || product.hsnCode || product.hsn || "N/A",
              gstRate: product.gstRate || product.gst || 0,
              igstRate: product.igstRate || 0,
              cessRate: product.cessRate || 0,
              groupName: groupName
            })
          }
        })
      }
      
      setProducts(productsData)
      setProductDetails(productDetailsArray)
      
      loadSalesWithGroupMapping(encodedEmail)
    })
  }

  const loadSalesWithGroupMapping = (encodedEmail) => {
    const salesRef = ref(database, `users/${encodedEmail}/sales`)
    onValue(salesRef, (snapshot) => {
      const data = snapshot.val()
      if (data) {
       const salesRecords = []
Object.entries(data).forEach(([saleId, saleData]) => {
  if (saleData.items && Array.isArray(saleData.items)) {
    saleData.items.forEach((item) => {
      salesRecords.push({
        invoiceNo: saleData.billNo || "N/A",
        invoiceDate: saleData.date || "",
        store: saleData.store || "Unknown Store",
        customer: saleData.customer || "Unknown Customer",
        productName: item.productName || item.itemDescription || "Unknown Product", // Use productName
        groupName: item.groupName || "No Group Assigned",
        hsnCode: item.hsnCode || "N/A",
        qty: item.qty,
        pRate: item.pRate || item.unitRate,
        grossAmt: item.grossAmt,
        discAmt: item.discAmt,
        cgstRate: item.cgstRate,
        sgstRate: item.sgstRate,
        igstRate: item.igstRate,
        cessRate: item.cessRate,
        netValue: item.netValue,
      })
    })
  }
})
setSalesData(salesRecords)
        setSalesData(salesRecords)
      } else {
        setSalesData([])
      }
    })
  }

  const handleFilterChange = (key, value) => {
    setFilters({ ...filters, [key]: value })
  }

  const handleGenerateReport = () => {
    setShowReport(true)
  }

  const handlePrint = () => {
    const printContent = (
      <SalesProductReportDisplay
        formData={{ fromDate, toDate, searchBy, reportType: "abstract" }}
        filters={filters}
        branchProfile={branchProfile}
        salesData={salesData}
        productDetails={productDetails}
      />
    );
  
    const htmlString = ReactDOMServer.renderToString(printContent);
  
    const printWindow = window.open('', '', 'width=1200,height=800');
    printWindow.document.write(`
      <html>
        <head>
          <title>Sales Productwise HSN Report</title>
          <style>
            body { 
              margin: 0; 
              padding: 5px; 
              font-family: Arial, sans-serif; 
              font-size: 8px;
            }
            @page { 
              size: A4 landscape; 
              margin: 5mm; 
            }
            table { 
              page-break-inside: auto;
              width: 100%;
              font-size: 7px;
              table-layout: fixed;
              border-collapse: collapse;
            }
            tr { 
              page-break-inside: avoid; 
              page-break-after: auto; 
            }
            th, td {
              padding: 2px !important;
              font-size: 7px !important;
              white-space: nowrap;
              overflow: hidden;
              text-overflow: ellipsis;
              max-width: 60px;
            }
            /* Specific column widths */
            th:nth-child(1), td:nth-child(1) { width: 30px; } /* S.No */
            th:nth-child(2), td:nth-child(2) { width: 80px; } /* Product Name */
            th:nth-child(3), td:nth-child(3) { width: 50px; } /* HSN Code */
            th:nth-child(4), td:nth-child(4) { width: 50px; } /* GST SLAB */
            th:nth-child(5), td:nth-child(5) { width: 50px; } /* CGST % */
            th:nth-child(6), td:nth-child(6) { width: 50px; } /* CGST AMT */
            th:nth-child(7), td:nth-child(7) { width: 50px; } /* SGST % */
            th:nth-child(8), td:nth-child(8) { width: 50px; } /* SGST AMT */
            th:nth-child(9), td:nth-child(9) { width: 50px; } /* IGST % */
            th:nth-child(10), td:nth-child(10) { width: 50px; } /* IGST AMT */
            th:nth-child(11), td:nth-child(11) { width: 50px; } /* GST AMT */
            th:nth-child(12), td:nth-child(12) { width: 50px; } /* CESS % */
            th:nth-child(13), td:nth-child(13) { width: 50px; } /* CESS AMT */
            th:nth-child(14), td:nth-child(14) { width: 50px; } /* Discount Amount */
            th:nth-child(15), td:nth-child(15) { width: 50px; } /* Gross Amount */
            th:nth-child(16), td:nth-child(16) { width: 60px; font-weight: bold; } /* Net Amount */
            .report-display {
              width: 93% !important;
              max-width: none !important;
              overflow: visible !important;
              2px solid #3b82f6 !important;
              padding: 15px !important;
            }
            div[style*="overflowX"] {
              overflow: visible !important;
            }
          </style>
        </head>
        <body>
          <div id="print-content">${htmlString}</div>
          <script>
            setTimeout(function() {
              window.print();
              window.close();
            }, 1000);
          </script>
        </body>
      </html>
    `);
    printWindow.document.close();
  };

  const formData = {
    fromDate,
    toDate,
    searchBy,
    reportType: "abstract",
  }

  if (loading) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "400px",
          fontSize: "18px",
          color: "#64748b",
          backgroundColor: "#f8fafc",
          borderRadius: "12px",
          border: "1px solid #e2e8f0",
        }}
      >
        <div style={{ textAlign: "center" }}>
          <div
            style={{
              width: "40px",
              height: "40px",
              border: "4px solid #e2e8f0",
              borderTop: "4px solid #3b82f6",
              borderRadius: "50%",
              animation: "spin 1s linear infinite",
              margin: "0 auto 16px",
            }}
          ></div>
          <div>Loading Sales Data...</div>
        </div>
      </div>
    )
  }

  if (!currentUser) {
    return (
      <div
        style={{
          textAlign: "center",
          padding: "3rem",
          color: "#64748b",
          fontSize: "18px",
          backgroundColor: "#f8fafc",
          borderRadius: "12px",
          border: "1px solid #e2e8f0",
        }}
      >
        <h2 style={{ color: "#1e293b", marginBottom: "16px" }}>Authentication Required</h2>
        <p>Please login to access the Sales Productwise HSN Report Generator</p>
      </div>
    )
  }

  return (
    <div
      style={{
        width: "100%",
        maxWidth: "90rem",
        backgroundColor: "white",
        borderRadius: "16px",
        boxShadow: "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)",
        padding: "32px",
        margin: "0 auto",
        fontFamily: "system-ui, -apple-system, sans-serif",
        border: "1px solid #e2e8f0",
      }}
    >
      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>

      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: "12px",
          marginBottom: "24px",
          paddingBottom: "16px",
          borderBottom: "2px solid #e2e8f0",
        }}
      >
        <div
          style={{
            width: "6px",
            height: "32px",
            backgroundColor: "#3b82f6",
            borderRadius: "3px",
          }}
        ></div>
        <h1
          style={{
            fontSize: "28px",
            fontWeight: "700",
            color: "#1e293b",
            margin: "0",
          }}
        >
          Productwise (HSN)_1A
        </h1>
      </div>

      {/* Date Range and Search Filters */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
          gap: "20px",
          marginBottom: "32px",
          padding: "24px",
          backgroundColor: "#f8fafc",
          borderRadius: "12px",
          border: "1px solid #e2e8f0",
        }}
      >
        <div>
          <label
            style={{
              display: "block",
              fontSize: "14px",
              fontWeight: "600",
              color: "#374151",
              marginBottom: "8px",
            }}
          >
            From Date
          </label>
          <input
            type="date"
            value={fromDate}
            onChange={(e) => setFromDate(e.target.value)}
            style={{
              width: "100%",
              padding: "12px 16px",
              border: "1px solid #d1d5db",
              borderRadius: "8px",
              fontSize: "14px",
              outline: "none",
              transition: "all 0.2s",
              backgroundColor: "white",
              boxSizing: "border-box",
            }}
            onFocus={(e) => (e.target.style.borderColor = "#3b82f6")}
            onBlur={(e) => (e.target.style.borderColor = "#d1d5db")}
          />
        </div>

        <div>
          <label
            style={{
              display: "block",
              fontSize: "14px",
              fontWeight: "600",
              color: "#374151",
              marginBottom: "8px",
            }}
          >
            To Date
          </label>
          <input
            type="date"
            value={toDate}
            onChange={(e) => setToDate(e.target.value)}
            style={{
              width: "100%",
              padding: "12px 16px",
              border: "1px solid #d1d5db",
              borderRadius: "8px",
              fontSize: "14px",
              outline: "none",
              transition: "all 0.2s",
              backgroundColor: "white",
              boxSizing: "border-box",
            }}
            onFocus={(e) => (e.target.style.borderColor = "#3b82f6")}
            onBlur={(e) => (e.target.style.borderColor = "#d1d5db")}
          />
        </div>

        <div>
          <label
            style={{
              display: "block",
              fontSize: "14px",
              fontWeight: "600",
              color: "#374151",
              marginBottom: "8px",
            }}
          >
            Search By
          </label>
          <select
            value={searchBy}
            onChange={(e) => setSearchBy(e.target.value)}
            style={{
              width: "100%",
              padding: "12px 16px",
              border: "1px solid #d1d5db",
              borderRadius: "8px",
              fontSize: "14px",
              outline: "none",
              transition: "all 0.2s",
              backgroundColor: "white",
              cursor: "pointer",
              boxSizing: "border-box",
            }}
            onFocus={(e) => (e.target.style.borderColor = "#3b82f6")}
            onBlur={(e) => (e.target.style.borderColor = "#d1d5db")}
          >
            <option value="Productwise">Productwise</option>
          </select>
        </div>

        <div>
          <label
            style={{
              display: "block",
              fontSize: "14px",
              fontWeight: "600",
              color: "#374151",
              marginBottom: "8px",
            }}
          >
            Report Type
          </label>
          <div
            style={{
              width: "100%",
              padding: "12px 16px",
              border: "1px solid #d1d5db",
              borderRadius: "8px",
              fontSize: "14px",
              backgroundColor: "#f8fafc",
              color: "#374151",
              boxSizing: "border-box",
            }}
          >
            Abstract
          </div>
        </div>
      </div>

      {/* Filter Checkboxes */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(250px, 1fr))",
          gap: "20px",
          marginBottom: "32px",
        }}
      >
        <FilterSection
          title="Customer"
          options={customers}
          selected={filters.customer}
          onChange={(value) => handleFilterChange("customer", value)}
        />
        <FilterSection
          title="Group"
          options={groups}
          selected={filters.group}
          onChange={(value) => handleFilterChange("group", value)}
        />
        <FilterSection
          title="Product"
          options={products}
          selected={filters.product}
          onChange={(value) => handleFilterChange("product", value)}
        />
      </div>

      {/* Action Buttons */}
      <div
        style={{
          display: "flex",
          gap: "16px",
          marginBottom: "32px",
          justifyContent: "center",
        }}
      >
        <button
          onClick={handleGenerateReport}
          style={{
            background: "linear-gradient(135deg, #16a34a 0%, #15803d 100%)",
            color: "white",
            padding: "12px 24px",
            borderRadius: "8px",
            border: "none",
            cursor: "pointer",
            transition: "all 0.3s",
            fontSize: "16px",
            fontWeight: "600",
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            minWidth: "140px",
            boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = "translateY(-2px)"
            e.currentTarget.style.boxShadow = "0 6px 12px rgba(0, 0, 0, 0.15)"
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = "translateY(0)"
            e.currentTarget.style.boxShadow = "0 4px 6px rgba(0, 0, 0, 0.1)"
          }}
        >
          Generate Report
        </button>

        <button
          onClick={handlePrint}
          style={{
            background: "linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)",
            color: "white",
            padding: "12px 24px",
            borderRadius: "8px",
            border: "none",
            cursor: "pointer",
            transition: "all 0.3s",
            fontSize: "16px",
            fontWeight: "600",
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            minWidth: "120px",
            boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = "translateY(-2px)"
            e.currentTarget.style.boxShadow = "0 6px 12px rgba(0, 0, 0, 0.15)"
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = "translateY(0)"
            e.currentTarget.style.boxShadow = "0 4px 6px rgba(0, 0, 0, 0.1)"
          }}
        >
          Print Report
        </button>
      </div>

      {/* Report Display */}
      {showReport && (
        <SalesProductReportDisplay
          formData={formData}
          filters={filters}
          branchProfile={branchProfile}
          salesData={salesData}
          productDetails={productDetails}
        />
      )}
    </div>
  )
}

export default SalesProductReportHSN_1A